/**
 * Focus Flow · Mission API client (Milestone 37.1).
 *
 * Every mutation carries the ``expected_version`` returned by the
 * previous read/write. A 409 from the server indicates a newer version
 * exists — callers must refetch and re-apply, and the LS mirror is
 * refreshed with the server payload.
 */
import { http } from "@/shared/api/http";
import type {
  Mission,
  MissionCompleteResponse,
  MissionEventEntry,
  MissionProgress,
  MissionStage,
} from "./types";

const base = (workspaceId: string) => `/workspaces/${workspaceId}/missions`;

export async function startMission(
  workspaceId: string,
  missionType: string,
  input: {
    subject_id?: string | null;
    initial_memory?: Record<string, unknown> | null;
    priority?: number;
  } = {},
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionType}/start`, {
    method: "POST",
    body: input,
  });
}

export async function getMission(
  workspaceId: string,
  missionId: string,
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionId}`);
}

export async function advanceMission(
  workspaceId: string,
  missionId: string,
  input: {
    expected_version: number;
    next_stage?: MissionStage;
    progress?: MissionProgress;
    memory_patch?: Record<string, unknown>;
  },
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionId}/state`, {
    method: "PATCH",
    body: input,
  });
}

export async function pauseMission(
  workspaceId: string,
  missionId: string,
  expected_version: number,
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionId}/pause`, {
    method: "POST",
    body: { expected_version },
  });
}

export async function resumeMission(
  workspaceId: string,
  missionId: string,
  expected_version: number,
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionId}/resume`, {
    method: "POST",
    body: { expected_version },
  });
}

export async function completeMission(
  workspaceId: string,
  missionId: string,
  input: {
    expected_version: number;
    final_memory_patch?: Record<string, unknown>;
  },
): Promise<MissionCompleteResponse> {
  return http<MissionCompleteResponse>(
    `${base(workspaceId)}/${missionId}/complete`,
    { method: "POST", body: input },
  );
}

export async function cancelMission(
  workspaceId: string,
  missionId: string,
  expected_version: number,
): Promise<Mission> {
  return http<Mission>(`${base(workspaceId)}/${missionId}/cancel`, {
    method: "POST",
    body: { expected_version },
  });
}

export async function listResumableMissions(
  workspaceId: string,
  limit = 25,
): Promise<{ items: Mission[] }> {
  return http<{ items: Mission[] }>(
    `${base(workspaceId)}?resumable=true&limit=${limit}`,
  );
}

export async function listMissionEvents(
  workspaceId: string,
  missionId: string,
): Promise<MissionEventEntry[]> {
  return http<MissionEventEntry[]>(`${base(workspaceId)}/${missionId}/events`);
}

// E2E-only helpers.
export async function isFocusFlowDemoEnabled(): Promise<boolean> {
  try {
    const r = await http<{ enabled: boolean }>("/e2e/focus-flow/enabled");
    return Boolean(r.enabled);
  } catch {
    return false;
  }
}

export async function cleanupDemoMissions(workspaceId: string): Promise<void> {
  try {
    await http<void>(`/e2e/focus-flow/workspaces/${workspaceId}/cleanup`, {
      method: "DELETE",
    });
  } catch {
    // Best-effort — teardown never blocks a spec.
  }
}
